import React, { useState, useEffect } from 'react';
import { Appointment, ProviderAvailability, DateSpecificAvailability, TimeSlot as FirebaseTimeSlot, firebaseAppointmentService } from '../services/firebase/FirebaseAppointmentService';
import DateSpecificAvailabilityModal from './DateSpecificAvailabilityModal';

interface TimeBlockViewProps {
  selectedDate: Date;
  availability: ProviderAvailability[];
  appointments: Appointment[];
  onSlotClick?: (startTime: string, endTime: string) => void;
  readOnly?: boolean;
  showPatientNames?: boolean;
  providerId?: string;
  onAvailabilityChange?: () => void;
  hideOverrideButton?: boolean; // New prop to hide the override button for clients
  useFirestore?: boolean; // When true, generate slots from availability prop instead of localStorage
  appointmentDuration?: number; // Override slot duration for client booking (e.g. 90 min initial consultation)
  filterByAppointmentType?: string; // Only show slots from blocks that include this appointment type
}

interface TimeSlot {
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  appointment?: Appointment;
  availabilityInfo?: ProviderAvailability;
}

const TimeBlockView: React.FC<TimeBlockViewProps> = ({
  selectedDate,
  availability,
  appointments,
  onSlotClick,
  readOnly = false,
  showPatientNames = true,
  providerId,
  onAvailabilityChange,
  hideOverrideButton = false,
  useFirestore = false,
  appointmentDuration,
  filterByAppointmentType
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dateOverride, setDateOverride] = useState<DateSpecificAvailability | null>(null);
  const [fallbackSlots, setFallbackSlots] = useState<FirebaseTimeSlot[]>([]);

  // Load date-specific override from Firestore
  useEffect(() => {
    if (useFirestore || !providerId) {
      setDateOverride(null);
      return;
    }
    firebaseAppointmentService.getDateSpecificAvailability(providerId, selectedDate)
      .then(override => setDateOverride(override))
      .catch(() => setDateOverride(null));
  }, [useFirestore, providerId, selectedDate]);

  // Load available slots from Firestore (non-Firestore-prop path)
  useEffect(() => {
    if (useFirestore || !providerId) {
      setFallbackSlots([]);
      return;
    }
    firebaseAppointmentService.getAvailableSlots(providerId, selectedDate)
      .then(slots => setFallbackSlots(slots))
      .catch(() => setFallbackSlots([]));
  }, [useFirestore, providerId, selectedDate]);

  // Check if selected date is today
  const isToday = (() => {
    const now = new Date();
    return selectedDate.getFullYear() === now.getFullYear() &&
           selectedDate.getMonth() === now.getMonth() &&
           selectedDate.getDate() === now.getDate();
  })();

  // Current time in minutes (for filtering past slots on today's date)
  const currentMinutes = (() => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  })();

  // Generate time slots from availability prop data (used when useFirestore=true)
  // Supports multi-block scheduling and appointment type filtering
  const generateSlotsFromAvailability = (): TimeSlot[] => {
    const dayOfWeek = selectedDate.getDay();
    const dayAvail = availability.find(a => a.dayOfWeek === dayOfWeek && a.isActive);
    if (!dayAvail) return [];

    const dateStr = selectedDate.toISOString().split('T')[0];
    const allSlots: TimeSlot[] = [];

    // Determine which blocks to generate slots from
    const blocks = dayAvail.blocks && dayAvail.blocks.length > 0
      ? dayAvail.blocks
      : [{ startTime: dayAvail.startTime, endTime: dayAvail.endTime, slotDuration: dayAvail.slotDuration, modalities: dayAvail.modalities, appointmentTypes: dayAvail.appointmentTypes || [] }];

    for (const block of blocks) {
      // Filter by appointment type if specified (client booking gateway)
      if (filterByAppointmentType && block.appointmentTypes && block.appointmentTypes.length > 0) {
        if (!block.appointmentTypes.includes(filterByAppointmentType as any)) continue;
      }

      const [startH, startM] = block.startTime.split(':').map(Number);
      const [endH, endM] = block.endTime.split(':').map(Number);
      const startMinutes = startH * 60 + startM;
      const endMinutes = endH * 60 + endM;
      const baseSlotDuration = block.slotDuration || 60;
      const displayDuration = appointmentDuration || baseSlotDuration;
      const bufferMinutes = 15;
      const slotIncrement = baseSlotDuration + bufferMinutes;

      for (let time = startMinutes; time < endMinutes; time += slotIncrement) {
        if (isToday && time < currentMinutes) continue;
        const slotStartH = Math.floor(time / 60);
        const slotStartM = time % 60;
        const slotEndTime = time + displayDuration;
        const slotEndH = Math.floor(slotEndTime / 60);
        const slotEndM = slotEndTime % 60;

        const startTime = `${slotStartH.toString().padStart(2, '0')}:${slotStartM.toString().padStart(2, '0')}`;
        const endTime = `${slotEndH.toString().padStart(2, '0')}:${slotEndM.toString().padStart(2, '0')}`;

        const appointment = appointments.find(appt => {
          const apptDateStr = new Date(appt.appointmentDate).toISOString().split('T')[0];
          return apptDateStr === dateStr && appt.startTime === startTime && appt.status !== 'cancelled';
        });

        allSlots.push({
          startTime,
          endTime,
          isAvailable: !appointment,
          appointment,
          availabilityInfo: dayAvail
        });
      }
    }

    return allSlots;
  };

  // Generate time slots using AppointmentService (localStorage)
  const generateTimeSlots = (): TimeSlot[] => {
    // Use Firestore-based slot generation from availability prop
    if (useFirestore) {
      return generateSlotsFromAvailability();
    }

    if (!providerId) {
      return [];
    }

    // Get available slots from Firestore (loaded via useEffect)
    const availableSlots = fallbackSlots;

    // Convert to TimeSlot format and check for appointments
    // Filter out past slots for today
    const filteredSlots = isToday
      ? availableSlots.filter(slot => {
          const [h, m] = slot.startTime.split(':').map(Number);
          return h * 60 + m >= currentMinutes;
        })
      : availableSlots;

    const timeSlots: TimeSlot[] = filteredSlots.map(slot => {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const appointment = appointments.find(appt => {
        const apptDateStr = new Date(appt.appointmentDate).toISOString().split('T')[0];
        return apptDateStr === dateStr && appt.startTime === slot.startTime && appt.status !== 'cancelled';
      });

      return {
        startTime: slot.startTime,
        endTime: slot.endTime,
        isAvailable: slot.available && !appointment,
        appointment,
        availabilityInfo: dateOverride && dateOverride.isAvailable
          ? {
              id: dateOverride.id,
              providerId: dateOverride.providerId,
              dayOfWeek: selectedDate.getDay(),
              startTime: dateOverride.startTime!,
              endTime: dateOverride.endTime!,
              modalities: dateOverride.modalities || ['video', 'in-person'],
              slotDuration: dateOverride.slotDuration || 60,
              isActive: true
            } as ProviderAvailability
          : availability.find(a => a.dayOfWeek === selectedDate.getDay() && a.isActive)
      };
    });

    return timeSlots;
  };

  const timeSlots = generateTimeSlots();

  const handleSlotClick = (slot: TimeSlot) => {
    if (readOnly || !slot.isAvailable || slot.appointment) return;
    onSlotClick?.(slot.startTime, slot.endTime);
  };

  const formatTime12Hour = (time24: string): string => {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const getSlotColor = (slot: TimeSlot): string => {
    if (slot.appointment) {
      // Has appointment
      if (slot.appointment.modality === 'video') return '#3b82f6'; // Blue for video
      return '#10b981'; // Green for in-person
    }
    if (slot.isAvailable) {
      return '#059669'; // Bright green for available
    }
    return '#1f2937'; // Darker gray for unavailable
  };

  const getSlotOpacity = (slot: TimeSlot): number => {
    if (slot.appointment) return 1;
    if (slot.isAvailable) return 1; // Full opacity for available slots
    return 0.4;
  };

  const getSlotBorder = (slot: TimeSlot): string => {
    if (slot.appointment) {
      return slot.appointment.modality === 'video' ? '2px solid #60a5fa' : '2px solid #34d399';
    }
    if (slot.isAvailable) {
      return '2px solid #34d399'; // Green border for available
    }
    return '1px solid #374151';
  };

  const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayName = weekDays[selectedDate.getDay()];
  const formattedDate = selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div style={{
      backgroundColor: '#1e293b',
      borderRadius: '8px',
      padding: '20px',
      color: '#f9fafb'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <div>
          <h3 style={{ margin: '0', fontSize: '18px' }}>
            {dayName}, {formattedDate}
          </h3>
          {dateOverride && (
            <div style={{
              fontSize: '12px',
              color: dateOverride.isAvailable ? '#fbbf24' : '#ef4444',
              marginTop: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {dateOverride.isAvailable ? '⚠️ Custom hours' : '🚫 Blocked'}
              {dateOverride.reason && `: ${dateOverride.reason}`}
            </div>
          )}
        </div>
        {providerId && !hideOverrideButton && (
          <button
            onClick={() => setIsModalOpen(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: dateOverride ? '#f59e0b' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = dateOverride ? '#d97706' : '#2563eb';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = dateOverride ? '#f59e0b' : '#3b82f6';
            }}
          >
            {dateOverride ? '✏️ Edit Override' : '+ Override Availability'}
          </button>
        )}
      </div>

      {/* Time Blocks Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: '12px'
      }}>
        {timeSlots.map((slot, index) => (
          <div
            key={index}
            onClick={() => handleSlotClick(slot)}
            style={{
              padding: '16px',
              backgroundColor: getSlotColor(slot),
              opacity: getSlotOpacity(slot),
              border: getSlotBorder(slot),
              borderRadius: '8px',
              cursor: (slot.isAvailable && !readOnly && !slot.appointment) ? 'pointer' : 'default',
              transition: 'all 0.2s',
              minHeight: '80px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              if (slot.isAvailable && !readOnly && !slot.appointment) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {/* Time */}
            <div style={{
              fontWeight: 'bold',
              fontSize: '16px',
              marginBottom: '8px'
            }}>
              {formatTime12Hour(slot.startTime)}
            </div>

            {/* Status/Content */}
            {slot.appointment ? (
              <div>
                <div style={{
                  fontSize: '14px',
                  marginBottom: '4px',
                  fontWeight: '600'
                }}>
                  {showPatientNames ? (slot.appointment.patientName || 'Patient') : 'Booked'}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#d1d5db',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {slot.appointment.modality === 'video' ? '📹 Video' : '👤 In-Person'}
                </div>
                <div style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  fontSize: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  padding: '2px 6px',
                  borderRadius: '4px'
                }}>
                  {slot.appointment.duration}min
                </div>
              </div>
            ) : slot.isAvailable ? (
              <div>
                <div style={{
                  fontSize: '13px',
                  color: '#6CA6C1',
                  fontWeight: '500'
                }}>
                  Available
                </div>
                {slot.availabilityInfo && (
                  <div style={{
                    fontSize: '11px',
                    color: '#d1d5db',
                    marginTop: '4px'
                  }}>
                    {slot.availabilityInfo.modalities.includes('video') && '📹 '}
                    {slot.availabilityInfo.modalities.includes('in-person') && '👤'}
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                fontSize: '13px',
                color: '#d1d5db',
                fontStyle: 'italic'
              }}>
                Unavailable
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        gap: '20px',
        marginTop: '20px',
        fontSize: '12px',
        color: '#d1d5db',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{
            width: '16px',
            height: '16px',
            backgroundColor: '#3b82f6',
            borderRadius: '4px',
            border: '2px solid #60a5fa'
          }} />
          <span>Video Appointment</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{
            width: '16px',
            height: '16px',
            backgroundColor: '#10b981',
            borderRadius: '4px',
            border: '2px solid #34d399'
          }} />
          <span>In-Person Appointment</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{
            width: '16px',
            height: '16px',
            backgroundColor: '#374151',
            borderRadius: '4px',
            border: '2px solid #6CA6C1',
            opacity: 0.8
          }} />
          <span>Available Slot</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{
            width: '16px',
            height: '16px',
            backgroundColor: '#1f2937',
            borderRadius: '4px',
            border: '1px solid #374151',
            opacity: 0.3
          }} />
          <span>Unavailable</span>
        </div>
      </div>

      {/* Date-Specific Availability Modal */}
      {providerId && (
        <DateSpecificAvailabilityModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          providerId={providerId}
          selectedDate={selectedDate}
          onSave={() => {
            onAvailabilityChange?.();
          }}
        />
      )}
    </div>
  );
};

export default TimeBlockView;
